/**
 * **Reading a QR code off a camera frame** — shared by the member app's connect scanner
 * (`MEM-028` AC-5) and the check-in screen (`EVT-042`).
 *
 * Built in the member app on 2026-09-11 and moved here unchanged when the door became the second
 * surface that needed it (`EVT-042` AC-6): two copies of a decoder would drift. Only the decoding
 * lives here. What a decoded string *means* — a connect token, a ticket — is each app's business.
 *
 * ## One API, two implementations, and the native one wins
 *
 * The browser's own **`BarcodeDetector`** is used wherever it exists and reports `qr_code` — which
 * is Chrome on Android, the single most common device in a room. It is hardware-accelerated, costs
 * nothing to ship, and is the API this module is shaped around.
 *
 * Where it does not exist — iOS Safari, Firefox, desktop Chrome — the fallback is
 * **`barcode-detector`** (Sec-ant, MIT, pinned at `3.2.2`), a *ponyfill of that same interface*.
 * That is why it was chosen over the alternatives:
 *
 *   - `jsqr` is the obvious pick and is **unmaintained**: its last publish is April 2021. This
 *     repository has already paid for one of those (`pdf-lib`), and a decoder standing between two
 *     people in a room is not the place to take the same bet.
 *   - `@zxing/library` is maintained but is a different API, so the native path and the fallback
 *     path would be two pieces of code to keep in step — and it is an order of magnitude larger.
 *   - `qr-scanner` (Nimiq) owns the camera as well as the decoding, which would take the
 *     viewfinder, the permission handling and the failure copy out of our hands. Last published
 *     2022.
 *
 * The ponyfill wraps `zxing-wasm`, so the fallback's decoder is the ZXing C++ reader compiled to
 * WebAssembly — the same engine `@zxing/library` ports, at a fraction of the JavaScript.
 *
 * ## The bundle cost, and where it lands
 *
 * **Nothing above is in the main bundle.** {@link loadQrDetector} reaches the ponyfill through a
 * dynamic `import()`, so the consuming app's Vite build emits it as its own chunk, fetched the
 * first time somebody opens a scanner **on a device that lacks the native API**. This module is
 * reached through the package's `./camera` entry rather than its root, so an app that never scans
 * (the internal console) never has it in its module graph at all.
 *
 * ## The `.wasm` is served from the app's own origin
 *
 * `zxing-wasm` fetches its binary from a public CDN by default. That is wrong twice over for both
 * surfaces: a room or a door with bad wifi is the *whole* use case, and a third-party fetch at that
 * moment is a dependency on somebody else's uptime and a request to somebody else's server carrying
 * our members' traffic. `wasmSource` is Vite's `?url` import, resolved by **the consuming app's**
 * Vite build from this package's own `zxing-wasm` dependency, so the file is emitted as an ordinary
 * hashed asset beside the app's chunk. `zxing-wasm` is therefore a **direct** dependency of this
 * package, pinned to the version `barcode-detector@3.2.2` resolves, so a lockfile change cannot leave
 * the override pointing at a binary the decoder will not load.
 *
 * **Both imports inside {@link loadQrDetector} are dynamic, and the `?url` one has to be.**
 * `tsx --test` runs the apps' and this package's pure rules straight off disk and resolves no Vite
 * suffix, so a top-level `import … from '…?url'` would make every test that reaches a scanner fail
 * to *load a module* rather than fail an assertion. Inside the factory it is parsed and never
 * evaluated there, because a test supplies the platform `BarcodeDetector` instead.
 */
const QR_FORMAT = 'qr_code';
/**
 * The platform's detector, if this engine has one that reads QR codes.
 *
 * `getSupportedFormats` is asked rather than assumed: a browser can ship `BarcodeDetector` with a
 * format list that does not include `qr_code`, and constructing one for a format it does not
 * support throws at `detect` time — in the middle of a conversation or a queue, which is the worst
 * possible moment to find out.
 */
async function nativeDetectorFactory() {
    const candidate = globalThis
        .BarcodeDetector;
    if (typeof candidate !== 'function')
        return null;
    try {
        const formats = await candidate.getSupportedFormats?.();
        if (formats !== undefined && !formats.includes(QR_FORMAT))
            return null;
        const detector = new candidate({ formats: [QR_FORMAT] });
        return {
            detect: async (frame) => (await detector.detect(frame)).map((found) => found.rawValue),
        };
    }
    catch {
        // A constructor that exists and refuses is a browser that cannot do this. Fall through to the
        // ponyfill rather than leaving somebody with a viewfinder that never resolves.
        return null;
    }
}
/**
 * The ponyfill, fetched on demand.
 *
 * The `import()` is inside the function on purpose: it is what makes this a separate chunk, and
 * therefore what keeps the decoder out of the bundle every other screen pays for.
 */
async function fallbackDetectorFactory() {
    const [{ BarcodeDetector, prepareZXingModule }, { default: wasmSource }] = await Promise.all([
        import('barcode-detector/ponyfill'),
        import('zxing-wasm/reader/zxing_reader.wasm?url'),
    ]);
    prepareZXingModule({
        overrides: {
            locateFile: (path, prefix) => path.endsWith('.wasm') ? wasmSource : `${prefix}${path}`,
        },
    });
    const detector = new BarcodeDetector({ formats: ['qr_code'] });
    return {
        detect: async (frame) => (await detector.detect(frame)).map((found) => found.rawValue),
    };
}
/**
 * The detector this device should use, loaded once.
 *
 * Memoised: a scanner can be opened and closed a dozen times in an evening, and neither the
 * capability check nor the WebAssembly instantiation should happen twice. A **failed** load is not
 * memoised — somebody who lost the network mid-fetch gets another attempt on their next tap.
 */
let pending = null;
export function loadQrDetector() {
    pending ??= (async () => {
        const native = await nativeDetectorFactory();
        if (native !== null)
            return { kind: 'native', detect: native.detect };
        const fallback = await fallbackDetectorFactory();
        return { kind: 'fallback', detect: fallback.detect };
    })().catch((error) => {
        pending = null;
        throw error;
    });
    return pending;
}
/** Test seam: forget the memoised detector. Never called by an app. */
export function resetQrDetectorForTests() {
    pending = null;
}
//# sourceMappingURL=qr-decoder.js.map